import { supabase, resolveFunctionPayload, describeFunctionError } from "./supabase";
import { todayISO } from "./localDate";
import { toast } from "sonner";

// Mock/Bridge client replacing Axios with Supabase
const client = {
  interceptors: {
    request: { use: () => {} },
    response: { use: () => {} }
  }
};

// Helper to map Supabase database rows (id -> _id) so frontend MongoDB logic doesn't break
function mapToMongo(data) {
  if (data === null || data === undefined) return data;
  if (Array.isArray(data)) {
    return data.map(item => mapToMongo(item));
  }
  if (typeof data === "object") {
    const mapped = { ...data };
    if (mapped.id) {
      mapped._id = mapped.id;
    }
    // Deep map nested objects if any
    for (const key in mapped) {
      if (mapped[key] && typeof mapped[key] === "object") {
        mapped[key] = mapToMongo(mapped[key]);
      }
    }
    return mapped;
  }
  return data;
}

// Helper to map incoming frontend bodies (_id -> id) for Supabase compatibility
function mapToPostgres(data) {
  if (data === null || data === undefined) return data;
  if (Array.isArray(data)) {
    return data.map(item => mapToPostgres(item));
  }
  if (typeof data === "object") {
    const mapped = { ...data };
    if (mapped._id) {
      mapped.id = mapped._id;
      delete mapped._id;
    }
    // Delete any MongoDB specific fields
    delete mapped.__v;
    return mapped;
  }
  return data;
}

// Match table name from API url path
function getTableName(url) {
  const clean = url.split("?")[0].replace(/^\/api/, "");
  if (clean.startsWith("/projects")) return "projects";
  if (clean.startsWith("/initiatives")) return "initiatives";
  if (clean.startsWith("/tasks")) return "tasks";
  if (clean.startsWith("/users")) return "users";
  if (clean.startsWith("/leaves")) return "leaves";
  if (clean.startsWith("/reports")) return "reports";
  if (clean.startsWith("/digest")) return "digests";
  if (clean.startsWith("/chat/conversations")) return "chat_conversations";
  if (clean.startsWith("/chat/messages")) return "chat_messages";
  if (clean.startsWith("/invites")) return "invites";
  if (clean.startsWith("/issues")) return "issues";
  if (clean.startsWith("/portal-apps")) return "portal_apps";
  return null;
}

// leaves has no `days`/`user_name`/`user_role` columns — those were being
// written on insert and read back on every list/summary endpoint below,
// which meant every single leave-creation attempt failed outright (a
// Postgres "column does not exist" error) and the table stayed empty since
// this feature was built. `days` is derived here instead of stored;
// user_name/user_role are looked up from users_public at read time instead
// of denormalized onto the row (avoids them going stale if a name changes).
//
// The people the signed-in user may see: themselves, plus the owners/members of
// every project they can access and of every initiative they are assigned to
// (team_visible_user_ids(), schema_scope_users_leaves_skills_to_initiatives.sql).
// Returns null for an admin, meaning "everyone". Plain pickers (assignee,
// project manager, leave approver) do NOT use this — they need the full roster.
async function getVisibleUserIds() {
  const { data: { user } } = await supabase.auth.getUser();
  const { data: me } = await supabase.from("users_public").select("role").eq("id", user?.id).single();
  if (me?.role === "admin") return null;
  const { data, error } = await supabase.rpc("team_visible_user_ids");
  if (error) throw error;
  return new Set((data || []).map((r) => r.id));
}

// Company public holidays (public.public_holidays, schema_public_holidays.sql)
// inside the range don't count — nobody applies leave for those, the whole
// company is off. Weekends still count, as they always have (explicit
// choice). `holidays` is a Set of "yyyy-mm-dd" strings; leave it out to get
// the plain calendar-day count.
function computeLeaveDays(startDate, endDate, holidays = null) {
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;
  const calendarDays = Math.max(1, Math.round((end - start) / 86400000) + 1);
  if (!holidays || holidays.size === 0) return calendarDays;
  let off = 0;
  // ISO yyyy-mm-dd, so a string compare IS a date compare.
  holidays.forEach((d) => { if (d >= startDate && d <= endDate) off++; });
  return Math.max(0, calendarDays - off);
}

// The holiday dates, as the Set computeLeaveDays wants. A failure (e.g. the
// table not created yet) degrades to "no holidays" rather than breaking
// every leave screen.
async function loadHolidayDates() {
  const { data, error } = await supabase.from("public_holidays").select("holiday_date");
  if (error) return new Set();
  return new Set((data || []).map((h) => h.holiday_date));
}

/*
  Calls an Edge Function and raises the REASON it failed.

  supabase.functions.invoke() reports any non-2xx as the same opaque
  "Edge Function returned a non-2xx status code" — the actual message the
  function returned ("forbidden: admin/manager only", a Graph rejection, a
  missing recipient) sits unread in the response body on error.context. Without
  reading it, every digest failure looked identical and undebuggable.
*/
async function invokeFunction(name, body) {
  const { data, error } = await supabase.functions.invoke(name, { body });
  if (error) {
    let detail = error.message || String(error);
    try {
      const res = error.context;
      if (res && typeof res.json === "function") {
        const payload = await res.json();
        if (payload?.error) {
          detail = typeof payload.error === "string" ? payload.error : JSON.stringify(payload.error);
        }
      }
    } catch { /* body not JSON, or already consumed */ }
    throw new Error(`${name}: ${detail}`);
  }
  // A function can also answer 200 with an { error } payload.
  if (data?.error) throw new Error(typeof data.error === "string" ? data.error : JSON.stringify(data.error));
  return data;
}

/*
  The digests table and the Digest page disagree about column names, the same
  way reports did: the row has `content` / `generated_at` / `delivered_to`, the
  page reads `markdown` / `created_at` / `recipients` / `sent_at`. Reshaped in
  one place here rather than renaming columns out from under the weekly-digest
  edge function, which writes `content`.

  There is no sent_at COLUMN — a send stamps metadata.sent_at instead, so the
  history list can show a tick without a migration.
*/
function shapeDigest(row) {
  if (!row) return row;
  const meta = row.metadata || {};
  return {
    ...row,
    markdown: row.content || "",
    created_at: row.generated_at,
    recipients: Array.isArray(row.delivered_to) ? row.delivered_to : [],
    sent_at: meta.sent_at || null,
  };
}

// digest_schedule stores a cron expression ("0 9 * * 6"), but the UI edits a
// day/hour/minute. Translated both ways here. pg_cron evaluates in UTC, and the
// table has no timezone column, so UTC is reported rather than invented.
const CRON_DOW = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

function scheduleFromRow(row) {
  const cron = (row?.cron_expr || "0 9 * * 6").trim().split(/\s+/);
  const minute = Number(cron[0]);
  const hour = Number(cron[1]);
  const dow = Number(cron[4]);
  return {
    id: row?.id || null,
    enabled: !!row?.enabled,
    recipients: Array.isArray(row?.recipients) ? row.recipients : [],
    cc: Array.isArray(row?.cc) ? row.cc : [],
    bcc: Array.isArray(row?.bcc) ? row.bcc : [],
    day_of_week: CRON_DOW[Number.isFinite(dow) ? dow % 7 : 6] || "sat",
    hour: Number.isFinite(hour) ? hour : 9,
    minute: Number.isFinite(minute) ? minute : 0,
    timezone: "UTC",
    // Absent until schema_digest_schedule_scope.sql is applied; [] means the
    // whole portfolio either way.
    scope_initiative_ids: row?.scope_initiative_ids || [],
    scope_project_ids: row?.scope_project_ids || [],
    scope_user_ids: row?.scope_user_ids || [],
    // Written by run-scheduled-comms (the pg_cron dispatcher), not by this
    // app directly — absent until the schedule has actually fired once.
    last_run_at: row?.last_run_at || null,
    last_status: row?.last_status || null,
  };
}

// report_schedules stores day_of_week/hour/minute directly (unlike
// digest_schedule's cron_expr), so this is a straight pass-through with
// defaults for "no schedule saved yet" (manage-report-schedule's "get"
// action returns { schedule: null } in that case).
function reportScheduleFromRow(row) {
  return {
    id: row?.id || null,
    // Needed by the "Saved schedules" list (one row per target across every
    // project/member, not just whichever is selected right now) to resolve
    // a name — absent from the single-target "get" caller's own request,
    // which already knows its own scope_type/scope_id, so this is additive.
    scope_type: row?.scope_type || null,
    scope_id: row?.scope_id || null,
    enabled: !!row?.enabled,
    recipients: Array.isArray(row?.recipients) ? row.recipients : [],
    cc: Array.isArray(row?.cc) ? row.cc : [],
    bcc: Array.isArray(row?.bcc) ? row.bcc : [],
    day_of_week: row?.day_of_week || "sat",
    hour: Number.isFinite(row?.hour) ? row.hour : 9,
    minute: Number.isFinite(row?.minute) ? row.minute : 0,
    last_run_at: row?.last_run_at || null,
    last_status: row?.last_status || null,
  };
}

/*
  Runs the weekly-digest edge function and returns the row it wrote.

  `dry_run: true` is deliberate: the function persists the digest BEFORE it
  checks that flag, so a dry run still creates the row — it only skips the
  function's own built-in Resend email, which this project does not use (all
  mail goes through Microsoft Graph). Sending is a separate, explicit action.
*/
async function runWeeklyDigest(scope) {
  const data = await invokeFunction("weekly-digest", { dry_run: true, scope: scope || {} });
  if (!data?.digest_id) throw new Error("weekly-digest returned no digest id");
  const { data: row, error: readErr } = await supabase.from("digests").select("*").eq("id", data.digest_id).single();
  if (readErr) throw readErr;
  // mapToMongo, not just shapeDigest: every row this shim hands the UI carries
  // `_id`, and the Digest page sends selected._id when emailing. Returning the
  // bare shaped row made a freshly generated digest the one object on the page
  // without an _id, so pressing Send right after Generate failed with
  // "digest_id is required" while sending an older digest from History worked.
  return mapToMongo(shapeDigest(row));
}

async function enrichLeaves(leaves) {
  const rows = leaves || [];
  const ids = [...new Set(rows.map((l) => l.user_id).filter(Boolean))];
  let userMap = {};
  const holidays = await loadHolidayDates();
  if (ids.length > 0) {
    const { data: users } = await supabase.from("users_public").select("id, name, role").in("id", ids);
    userMap = Object.fromEntries((users || []).map((u) => [u.id, u]));
  }
  return rows.map((l) => ({
    ...l,
    user_name: userMap[l.user_id]?.name || "Unknown",
    user_role: userMap[l.user_id]?.role || "member",
    days: computeLeaveDays(l.start_date, l.end_date, holidays),
  }));
}

// Helper to extract the trailing UUID from URL paths (e.g. /projects/123 -> 123)
function getResourceId(url, table) {
  const parts = url.split("?")[0].split("/");
  // Return the part that is a UUID or matches the resource location
  for (const part of parts) {
    if (part.length >= 36) return part; // standard uuid length is 36 chars
  }
  return null;
}

// Emulate Axios GET requests via Supabase client
client.get = async function(url, config) {

  // Company public holidays, oldest first. Readable by anyone signed in.
  if (url === "/holidays") {
    const { data, error } = await supabase
      .from("public_holidays")
      .select("*")
      .order("holiday_date", { ascending: true });
    if (error) throw error;
    return { data: mapToMongo(data) };
  }

  if (url === "/portal-apps") {
    const { data, error } = await supabase
      .from("portal_apps")
      .select("*")
      .order("sort_order", { ascending: true })
      .order("title", { ascending: true });
    if (error) throw error;
    return { data: mapToMongo(data) };
  }

  // 1. Handle special custom stats/config routes
  if (url === "/stats/overview") {
    const { data: initiatives } = await supabase.from("initiatives").select("id, status");
    const { data: projects } = await supabase.from("projects").select("id, status");
    const { data: tasks } = await supabase.from("tasks").select("id, status, completed_at, assignee_id, priority");
    // users (base table) has no SELECT grant for anon/authenticated anymore —
    // users_public is the only readable shape (id/email/name/role/avatar_url/disabled/created_at).
    const { data: allUsers } = await supabase.from("users_public").select("id, name, email");
    // The dashboard counts and workload cover only the people in the caller's
    // initiatives (an admin: everyone). Projects and tasks are already scoped by RLS.
    const visibleIds = await getVisibleUserIds();
    const users = visibleIds ? (allUsers || []).filter((u) => visibleIds.has(u.id)) : allUsers;
    const nameById = Object.fromEntries((allUsers || []).map((u) => [u.id, u.name || u.email]));

    const statusCounts = { open: 0, wip: 0, test: 0, uat: 0, cutover: 0, live: 0, closed: 0, cancelled: 0, hold: 0 };
    tasks?.forEach(t => {
      statusCounts[t.status || "open"]++;
    });

    const completionSeries = [];
    const now = new Date();
    for (let i = 29; i >= 0; i--) {
      const d = new Date();
      d.setDate(now.getDate() - i);
      const isoDate = d.toISOString().split("T")[0];
      const completedOnDay = tasks?.filter(t => t.completed_at && t.completed_at.startsWith(isoDate)).length || 0;
      completionSeries.push({ date: isoDate, completed: completedOnDay });
    }

    const emptyWorkload = () => ({ total: 0, open: 0, wip: 0, test: 0, uat: 0, cutover: 0, live: 0, closed: 0, cancelled: 0, hold: 0 });
    const workloadMap = {};
    users?.forEach(u => {
      workloadMap[u.id] = { name: u.name || u.email, ...emptyWorkload() };
    });
    workloadMap["unassigned"] = { name: "Unassigned", ...emptyWorkload() };

    tasks?.forEach(t => {
      const aid = t.assignee_id || "unassigned";
      if (!workloadMap[aid]) {
        workloadMap[aid] = { name: nameById[aid] || "Unknown User", ...emptyWorkload() };
      }
      workloadMap[aid].total++;
      workloadMap[aid][t.status || "open"]++;
    });

    const workloadList = Object.keys(workloadMap).map(id => ({
      assignee_id: id === "unassigned" ? null : id,
      ...workloadMap[id]
    })).sort((a, b) => b.total - a.total);

    return {
      data: {
        counts: {
          initiatives: initiatives?.length || 0,
          projects: projects?.length || 0,
          tasks: tasks?.length || 0,
          users: users?.length || 0
        },
        status_distribution: [
          { status: "open", count: statusCounts.open },
          { status: "wip", count: statusCounts.wip },
          { status: "test", count: statusCounts.test },
          { status: "uat", count: statusCounts.uat },
          { status: "cutover", count: statusCounts.cutover },
          { status: "live", count: statusCounts.live },
          { status: "closed", count: statusCounts.closed },
          { status: "cancelled", count: statusCounts.cancelled },
          { status: "hold", count: statusCounts.hold }
        ],
        completion_series: completionSeries,
        workload: workloadList
      }
    };
  }

  // The Team page's own member list (2026-09-16, explicit request): a
  // manager or member only sees people who share at least one project with
  // them — not the whole org. Deliberately a SEPARATE route from plain
  // "/users": every other consumer of the roster (task/project assignee
  // pickers, "plan leave for a member", the project-manager/owner selects,
  // etc.) still calls "/users" below and stays fully unfiltered on purpose —
  // a manager may legitimately need to pick someone outside their current
  // projects there. Admins get the same unfiltered list here too; only
  // manager/member callers get narrowed. See
  // schema_team_page_project_scoping.sql for team_visible_user_ids().
  if (url === "/team/members") {
    const visibleIds = await getVisibleUserIds();
    if (visibleIds === null) {
      const { data, error } = await supabase.from("users_public").select("*");
      if (error) throw error;
      return { data: mapToMongo(data) };
    }
    const ids = [...visibleIds];
    if (ids.length === 0) return { data: [] };
    const { data, error } = await supabase.from("users_public").select("*").in("id", ids);
    if (error) throw error;
    return { data: mapToMongo(data) };
  }

  if (url.startsWith("/stats/member/")) {
    const uid = getResourceId(url);
    const { data: tasks } = await supabase.from("tasks").select("*").eq("assignee_id", uid);
    const statusCounts = { open: 0, wip: 0, test: 0, uat: 0, cutover: 0, live: 0, closed: 0, cancelled: 0, hold: 0 };
    tasks?.forEach(t => {
      statusCounts[t.status || "open"]++;
    });
    return {
      data: {
        task_count: tasks?.length || 0,
        status_distribution: [
          { status: "open", count: statusCounts.open },
          { status: "wip", count: statusCounts.wip },
          { status: "test", count: statusCounts.test },
          { status: "uat", count: statusCounts.uat },
          { status: "cutover", count: statusCounts.cutover },
          { status: "live", count: statusCounts.live },
          { status: "closed", count: statusCounts.closed },
          { status: "cancelled", count: statusCounts.cancelled },
          { status: "hold", count: statusCounts.hold }
        ],
        completion_series: []
      }
    };
  }

  /*
    Digest reads.

    All of these used to fall through to the generic table dispatch, which
    matched the "/digest" prefix and returned rows from the `digests` table for
    every one of them. So /digest/config handed the page an ARRAY of digests
    (making `config.resend_configured` undefined, which is why the RESEND
    warning showed permanently) and /digest/schedule did the same, leaving the
    DAY and HOUR selects blank and the enable toggle stuck off.
  */
  if (url === "/digest") {
    const { data, error } = await supabase.from("digests").select("*").order("generated_at", { ascending: false });
    if (error) throw error;
    // Same per-user archive lookup as /reports above — digest_archives, not a
    // shared column, so archiving is invisible to every other viewer.
    const { data: { user } } = await supabase.auth.getUser();
    let archivedIds = new Set();
    if (user?.id) {
      const { data: archives } = await supabase.from("digest_archives").select("digest_id").eq("user_id", user.id);
      archivedIds = new Set((archives || []).map((a) => a.digest_id));
    }
    const shaped = (data || []).map((r) => ({ ...shapeDigest(r), archived: archivedIds.has(r.id) }));
    return { data: mapToMongo(shaped) };
  }

  /*
    Every project membership in one call.

    The per-project route (/projects/:id/members) needs one request per project,
    which is fine on a detail page but not for a picker that has to know, for 41
    projects at once, who is on each. RLS still scopes the rows.
  */
  if (url === "/project-members") {
    const { data, error } = await supabase.from("project_members").select("project_id, user_id");
    if (error) throw error;
    return { data: data || [] };
  }

  /*
    Weekly delivery history — every attempt run-scheduled-comms evaluated as
    due (the real hourly cron sweep, or a "Test schedule now" click), not
    just the single most-recent last_run_at/last_status. Read directly
    (RLS-scoped, same actors who can already see the schedule itself), not
    through an Edge Function: this is a plain read with no privileged write
    attached to it.

    Checked BEFORE the exact/prefix matches below, since "/digest/schedule"
    and "/reports/schedule" are both prefixes of these URLs too.
  */
  if (url.startsWith("/digest/schedule/history")) {
    const { data, error } = await supabase
      .from("schedule_delivery_log")
      .select("*")
      .eq("schedule_type", "digest")
      .order("ran_at", { ascending: false })
      .limit(15);
    if (error) throw error;
    return { data: data || [] };
  }
  if (url.startsWith("/reports/schedule/history")) {
    const qs = new URLSearchParams(url.split("?")[1] || "");
    const schedule_id = qs.get("schedule_id");
    if (!schedule_id) return { data: [] };
    const { data, error } = await supabase
      .from("schedule_delivery_log")
      .select("*")
      .eq("schedule_type", "report")
      .eq("schedule_id", schedule_id)
      .order("ran_at", { ascending: false })
      .limit(15);
    if (error) throw error;
    return { data: data || [] };
  }

  /*
    Every report schedule the caller is allowed to see (RLS: is_admin() or
    is_report_scope_person() — the same rule "read reports" already uses),
    not just whichever project/member happens to be selected in the
    Generate panel right now. Checked before the singular "/reports/schedule"
    match below, since that one matches this URL as a prefix too.
  */
  if (url === "/reports/schedules") {
    const { data, error } = await supabase
      .from("report_schedules").select("*").order("updated_at", { ascending: false });
    if (error) throw error;
    return { data: (data || []).map(reportScheduleFromRow) };
  }

  if (url === "/digest/schedule") {
    // Read through the same Edge Function that writes it, so both sides agree
    // about which row is "the" schedule even before one exists.
    const data = await invokeFunction("manage-digest-schedule", { action: "get" });
    return { data: scheduleFromRow(data?.schedule) };
  }

  // Per-project/per-member report auto-schedule — one row per scope, so the
  // scope has to travel as query params rather than living in the path.
  if (url.startsWith("/reports/schedule")) {
    const qs = new URLSearchParams(url.split("?")[1] || "");
    const scope_type = qs.get("scope_type");
    const scope_id = qs.get("scope_id");
    if (!scope_type || !scope_id) return { data: null };
    const data = await invokeFunction("manage-report-schedule", { action: "get", scope_type, scope_id });
    return { data: reportScheduleFromRow(data?.schedule) };
  }

  // Handle specific Leaves endpoints
  if (url === "/leaves/summary") {
    const { data: leaves } = await supabase.from("leaves").select("*");
    const { data: users } = await supabase.from("users_public").select("id");
    const holidays = await loadHolidayDates();

    const per_status = { planned: 0, approved: 0, rejected: 0, taken: 0, cancelled: 0 };
    const userMap = {};
    users?.forEach(u => {
      userMap[u.id] = { user_id: u.id, total_days: 0, planned: 0, approved: 0, taken: 0 };
    });

    leaves?.forEach(l => {
      const status = l.status || "planned";
      if (status in per_status) per_status[status]++;
      
      const uid = l.user_id;
      if (uid) {
        if (!userMap[uid]) {
          userMap[uid] = { user_id: uid, total_days: 0, planned: 0, approved: 0, taken: 0 };
        }
        const days = computeLeaveDays(l.start_date, l.end_date, holidays);
        userMap[uid].total_days += days;
        if (status === "planned") userMap[uid].planned += days;
        if (status === "approved") userMap[uid].approved += days;
        if (status === "taken") userMap[uid].taken += days;
      }
    });

    return {
      data: {
        per_status,
        per_user: Object.values(userMap)
      }
    };
  }

  // Overlapping leave between two people who work on the SAME project.
  //
  // This used to `return { data: { conflicts: [] } }` — a stub — so the Leaves
  // page's Conflicts tab showed its "All clear" empty state permanently, no
  // matter how many people were off at once. The whole feature was inert.
  //
  // Computed here rather than in SQL because there is no server: RLS already
  // decides which leaves and projects the caller can see, so a member gets
  // conflicts only for their own projects and an admin/manager gets all of them.
  //
  // A project's people are its owner plus its project_members roster — the same
  // set the "read projects" policy admits, and the same set a task can now be
  // assigned to.
  if (url === "/leaves/conflicts") {
    const [leavesRes, projectsRes, membersRes] = await Promise.all([
      // Only leave that actually takes someone off the project counts:
      //  - rejected / cancelled never happen,
      //  - "wfh" is working, just not in the office, so two people on WFH is
      //    not a staffing clash,
      //  - "public_holiday" is everybody at once by definition; flagging it
      //    would bury the real conflicts under noise,
      //  - anything that already ended is history, not a clash to plan
      //    around — a leave whose end_date is before today used to keep
      //    showing here forever, since nothing ever re-checked the date
      //    after it passed.
      supabase.from("leaves").select("id, user_id, type, status, start_date, end_date")
        .in("status", ["planned", "approved", "taken"])
        .in("type", ["vacation", "sick", "personal", "other"])
        .gte("end_date", todayISO()),
      supabase.from("projects").select("id, title, owner_id"),
      supabase.from("project_members").select("project_id, user_id"),
    ]);

    const leaves = leavesRes.data || [];
    const projects = projectsRes.data || [];
    const holidays = await loadHolidayDates();

    // project_id -> the people on it (owner included, deduped)
    const peopleByProject = new Map();
    projects.forEach((p) => {
      peopleByProject.set(p.id, new Set(p.owner_id ? [p.owner_id] : []));
    });
    (membersRes.data || []).forEach((m) => {
      if (!peopleByProject.has(m.project_id)) peopleByProject.set(m.project_id, new Set());
      peopleByProject.get(m.project_id).add(m.user_id);
    });

    const leavesByUser = new Map();
    leaves.forEach((l) => {
      if (!l.user_id || !l.start_date || !l.end_date) return;
      if (!leavesByUser.has(l.user_id)) leavesByUser.set(l.user_id, []);
      leavesByUser.get(l.user_id).push(l);
    });

    const names = {};
    const involved = [...new Set(leaves.map((l) => l.user_id).filter(Boolean))];
    if (involved.length > 0) {
      const { data: us } = await supabase.from("users_public").select("id, name, email").in("id", involved);
      (us || []).forEach((u) => { names[u.id] = u.name || u.email || "Unknown"; });
    }

    const conflicts = [];
    const seen = new Set();

    projects.forEach((project) => {
      // Only people who actually have leave booked can clash.
      const people = [...(peopleByProject.get(project.id) || [])].filter((uid) => leavesByUser.has(uid));

      for (let i = 0; i < people.length; i++) {
        for (let j = i + 1; j < people.length; j++) {
          for (const a of leavesByUser.get(people[i])) {
            for (const b of leavesByUser.get(people[j])) {
              // ISO yyyy-mm-dd, so a string compare IS a date compare.
              const start = a.start_date > b.start_date ? a.start_date : b.start_date;
              const end = a.end_date < b.end_date ? a.end_date : b.end_date;
              if (start > end) continue;

              const key = `${project.id}|${a.id}|${b.id}`;
              if (seen.has(key)) continue;
              seen.add(key);

              // A clash that falls only on a company holiday isn't one —
              // everyone is off that day anyway.
              const overlapDays = computeLeaveDays(start, end, holidays);
              if (overlapDays === 0) continue;
              // Length drives the base risk; two people merely PLANNING time
              // off is softer than two approved absences, so an all-planned
              // pair drops a level.
              const bothPlanned = a.status === "planned" && b.status === "planned";
              let severity = overlapDays >= 3 ? "high" : overlapDays === 2 ? "medium" : "low";
              if (bothPlanned) severity = severity === "high" ? "medium" : "low";

              conflicts.push({
                project_id: project.id,
                project_title: project.title,
                member_a: { user_id: a.user_id, name: names[a.user_id] || "Unknown", leave_type: a.type, leave_status: a.status },
                member_b: { user_id: b.user_id, name: names[b.user_id] || "Unknown", leave_type: b.type, leave_status: b.status },
                overlap_start: start,
                overlap_end: end,
                overlap_days: overlapDays,
                severity,
              });
            }
          }
        }
      }
    });

    conflicts.sort((x, y) => x.overlap_start.localeCompare(y.overlap_start));
    return { data: { conflicts } };
  }

  // Must come before the generic getTableName dispatch below — that
  // matches on a plain prefix check, so "/chat/conversations/:id/messages"
  // was being read as "/chat/conversations" and returning a single
  // conversation row (via getResourceId grabbing the id in the URL)
  // instead of that conversation's messages — an object where the
  // frontend expected an array, hence "messages.map is not a function".
  if (/^\/chat\/conversations\/[^/]+\/messages$/.test(url)) {
    const convId = url.split("/")[3];
    const { data, error } = await supabase.from("chat_messages").select("*").eq("conversation_id", convId).order("created_at", { ascending: true });
    if (error) throw error;
    return { data: mapToMongo(data) };
  }

  // Real columns are scope_type/scope_id/generated_by/generated_at —
  // ReportsPage.jsx reads scope/target_id/created_by/created_at (same
  // mismatch as the insert above), so every existing report row would
  // have rendered with a blank scope label and date without this reshape.
  if (url === "/reports") {
    const { data, error } = await supabase.from("reports").select("*").order("generated_at", { ascending: false });
    if (error) throw error;
    // Archiving is personal/per-user (report_archives), not a shared column on
    // reports itself — a row archived by one viewer must stay untouched for
    // everyone else, so this is a separate lookup merged in by id, not a join
    // the RLS-scoped `reports` select could carry on its own.
    const { data: { user } } = await supabase.auth.getUser();
    let archivedIds = new Set();
    if (user?.id) {
      const { data: archives } = await supabase.from("report_archives").select("report_id").eq("user_id", user.id);
      archivedIds = new Set((archives || []).map((a) => a.report_id));
    }
    const reshaped = (data || []).map((r) => ({
      ...r,
      scope: r.scope_type,
      target_id: r.scope_id,
      created_by: r.generated_by,
      created_at: r.generated_at,
      // Who this report has already been emailed to, recorded by send-report in
      // metadata (reports has no delivered_to column, and does not need one).
      sent_at: r.metadata?.sent_at || null,
      sent_to: Array.isArray(r.metadata?.sent_to) ? r.metadata.sent_to : [],
      archived: archivedIds.has(r.id),
    }));
    return { data: mapToMongo(reshaped) };
  }

  // Plain leave list — RLS already scopes which rows come back (a member
  // only ever gets their own; admin/manager get everyone's), this just
  // attaches the display fields the UI expects (see enrichLeaves above).
  if (url === "/leaves") {
    const { data, error } = await supabase.from("leaves").select("*").order("start_date", { ascending: false });
    if (error) throw error;
    return { data: mapToMongo(await enrichLeaves(data)) };
  }

  if (url.startsWith("/users/") && url.endsWith("/leaves")) {
    const uid = url.split("/")[2];
    const { data, error } = await supabase.from("leaves").select("*").eq("user_id", uid).order("start_date", { ascending: false });
    if (error) throw error;
    return { data: mapToMongo(await enrichLeaves(data)) };
  }

  // Project/initiative membership — who can see one beyond its owner. Both
  // *_members tables have a composite key (no `id` column), so they can't
  // go through the generic getTableName/getResourceId path below; handled
  // explicitly here and in post/delete instead.
  if (/^\/projects\/[^/]+\/members$/.test(url) || /^\/initiatives\/[^/]+\/members$/.test(url)) {
    const isInitiative = url.startsWith("/initiatives/");
    const table = isInitiative ? "initiative_members" : "project_members";
    const fkCol = isInitiative ? "initiative_id" : "project_id";
    const parentId = url.split("/")[2];
    const { data: rows, error } = await supabase.from(table).select("*").eq(fkCol, parentId);
    if (error) throw error;
    const ids = (rows || []).map((r) => r.user_id);
    let userMap = {};
    if (ids.length > 0) {
      const { data: users } = await supabase.from("users_public").select("id, name, email, role").in("id", ids);
      userMap = Object.fromEntries((users || []).map((u) => [u.id, u]));
    }
    const merged = (rows || []).map((r) => ({
      _id: r.user_id,
      user_id: r.user_id,
      added_at: r.added_at,
      name: userMap[r.user_id]?.name || "Unknown",
      email: userMap[r.user_id]?.email || "",
      role: userMap[r.user_id]?.role || "member",
    }));
    return { data: merged };
  }

  // Ask AI is BYOK — never returns the raw key, only whether one's set.
  if (url === "/account/ai-key") {
    const { data, error } = await supabase.functions.invoke("manage-ai-key", { body: { action: "get_status" } });
    if (error) throw error;
    if (data?.error) throw new Error(typeof data.error === "string" ? data.error : JSON.stringify(data.error));
    return { data };
  }

  if (url === "/settings/admin-2fa-enforcement") {
    return { data: { enabled: false } };
  }
  if (url === "/auth/2fa/status") {
    return { data: { enabled: false } };
  }
  if (url === "/audit-logs/archives") {
    return { data: [] };
  }
  if (url === "/audit-logs") {
    return { data: [] };
  }
  if (url === "/audit-logs/actions") {
    return { data: [] };
  }
  // Tasks — always embeds assignee_ids (primary assignee_id plus anyone in
  // task_assignees, deduped) so multi-assignee UI doesn't need a second
  // round trip. The generic 2-below path's naive .eq("assignee_id", val)
  // would only ever match the primary assignee, missing anyone added as an
  // additional one — filtered here instead once both sources are in hand.
  if (/^\/tasks(\?.*)?$/.test(url)) {
    const urlObj = new URL(url, "http://localhost");
    const projectId = urlObj.searchParams.get("project_id");
    const assigneeId = urlObj.searchParams.get("assignee_id");
    const parentTaskId = urlObj.searchParams.get("parent_task_id");
    let query = supabase.from("tasks").select("*");
    if (projectId && projectId !== "null") query = query.eq("project_id", projectId);
    /*
      parent_task_id was NOT honoured here, and this route swallows any param it
      does not recognise — so /tasks?parent_task_id=X quietly returned EVERY
      task in the board. The task page read that as its own children and showed
      "Subtasks (283)" on every task, even though nothing had ever been linked
      (parent_task_id was null on every row).
    */
    if (parentTaskId && parentTaskId !== "null") query = query.eq("parent_task_id", parentTaskId);
    const { data: rows, error } = await query;
    if (error) throw error;
    const ids = (rows || []).map((t) => t.id);
    const byTask = {};
    if (ids.length > 0) {
      const { data: extra } = await supabase.from("task_assignees").select("task_id, user_id").in("task_id", ids);
      (extra || []).forEach((e) => { (byTask[e.task_id] ||= []).push(e.user_id); });
    }
    let result = (rows || []).map((t) => ({
      ...t,
      assignee_ids: [t.assignee_id, ...(byTask[t.id] || [])].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i),
    }));
    if (assigneeId && assigneeId !== "null") {
      result = result.filter((t) => t.assignee_ids.includes(assigneeId));
    }
    return { data: mapToMongo(result) };
  }

  // Single task detail page — same assignee_ids embedding as the list
  // route above, plus the project/initiative it rolls up under so the page
  // can show breadcrumbs without a second fetch. Must come before the
  // generic dispatch (which would return the bare row, no assignee_ids)
  // and is deliberately narrower than /tasks/:id/assignees or /comments.
  if (/^\/tasks\/[^/]+$/.test(url)) {
    const taskId = url.split("?")[0].split("/")[2];
    const { data: row, error } = await supabase.from("tasks").select("*").eq("id", taskId).single();
    if (error) throw error;
    const { data: extra } = await supabase.from("task_assignees").select("user_id").eq("task_id", taskId);
    const assignee_ids = [row.assignee_id, ...(extra || []).map((e) => e.user_id)].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i);
    return { data: mapToMongo({ ...row, assignee_ids }) };
  }

  // Latest "go Live" approval request for a task (null if none), with names and
  // whether the viewer may decide it — see schema_task_live_approval.sql.
  if (/^\/tasks\/[^/]+\/live-approval$/.test(url)) {
    const taskId = url.split("/")[2];
    const { data, error } = await supabase.rpc("get_live_approval", { p_task_id: taskId });
    if (error) throw error;
    return { data: data || null };
  }

  // Requests waiting for the viewer to approve or reject.
  if (url === "/live-approvals/mine") {
    const { data, error } = await supabase.rpc("my_live_approvals");
    if (error) throw error;
    return { data: data || [] };
  }

  // Task comments — newest-last so the thread reads top-to-bottom;
  // author_name is looked up from users_public (the only readable shape)
  // rather than denormalized onto the row.
  if (/^\/tasks\/[^/]+\/comments$/.test(url)) {
    const taskId = url.split("/")[2];
    const { data: rows, error } = await supabase.from("task_comments").select("*").eq("task_id", taskId).order("created_at", { ascending: true });
    if (error) throw error;
    const ids = [...new Set((rows || []).map((r) => r.user_id).filter(Boolean))];
    let userMap = {};
    if (ids.length > 0) {
      const { data: authors } = await supabase.from("users_public").select("id, name, email").in("id", ids);
      userMap = Object.fromEntries((authors || []).map((u) => [u.id, u]));
    }
    const merged = (rows || []).map((r) => ({
      ...r,
      author_name: userMap[r.user_id]?.name || userMap[r.user_id]?.email || "Unknown",
    }));
    return { data: mapToMongo(merged) };
  }

  // Task lifecycle log — one row per status/priority/assignee/due-date/title/
  // description change plus a "created" row, all written server-side by
  // triggers (see task_activity_log_2026_08_11 migration), never by the
  // client. actor_name/from_name/to_name are resolved here for any value
  // that's a user id (assignee-related actions); status/priority/due-date/
  // title stay as raw values for the frontend to format with its own labels.
  if (/^\/tasks\/[^/]+\/activity$/.test(url)) {
    const taskId = url.split("/")[2];
    const { data: rows, error } = await supabase.from("task_activity").select("*").eq("task_id", taskId).order("created_at", { ascending: true });
    if (error) throw error;
    const uuidRe = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const ids = new Set();
    (rows || []).forEach((r) => {
      if (r.actor_id) ids.add(r.actor_id);
      if (r.from_value && uuidRe.test(r.from_value)) ids.add(r.from_value);
      if (r.to_value && uuidRe.test(r.to_value)) ids.add(r.to_value);
    });
    let userMap = {};
    if (ids.size > 0) {
      const { data: authors } = await supabase.from("users_public").select("id, name, email").in("id", [...ids]);
      userMap = Object.fromEntries((authors || []).map((u) => [u.id, u.name || u.email]));
    }
    const merged = (rows || []).map((r) => ({
      ...r,
      actor_name: userMap[r.actor_id] || "Someone",
      from_name: r.from_value && userMap[r.from_value] ? userMap[r.from_value] : undefined,
      to_name: r.to_value && userMap[r.to_value] ? userMap[r.to_value] : undefined,
    }));
    return { data: mapToMongo(merged) };
  }

  if (/^\/tasks\/[^/]+\/subtask-activity$/.test(url)) {
    const parentId = url.split("/")[2];
    const { data: kids, error: kidsError } = await supabase.from("tasks").select("id, title").eq("parent_task_id", parentId);
    if (kidsError) throw kidsError;
    const kidIds = (kids || []).map((k) => k.id);
    if (kidIds.length === 0) return { data: [] };
    const titleById = Object.fromEntries((kids || []).map((k) => [k.id, k.title]));
    const { data: rows, error } = await supabase.from("task_activity").select("*").in("task_id", kidIds).neq("action", "created").order("created_at", { ascending: true });
    if (error) throw error;
    const uuidRe = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const ids = new Set();
    (rows || []).forEach((r) => {
      if (r.actor_id) ids.add(r.actor_id);
      if (r.from_value && uuidRe.test(r.from_value)) ids.add(r.from_value);
      if (r.to_value && uuidRe.test(r.to_value)) ids.add(r.to_value);
    });
    let userMap = {};
    if (ids.size > 0) {
      const { data: authors } = await supabase.from("users_public").select("id, name, email").in("id", [...ids]);
      userMap = Object.fromEntries((authors || []).map((u) => [u.id, u.name || u.email]));
    }
    const merged = (rows || []).map((r) => ({
      ...r,
      actor_name: userMap[r.actor_id] || "Someone",
      from_name: r.from_value && userMap[r.from_value] ? userMap[r.from_value] : undefined,
      to_name: r.to_value && userMap[r.to_value] ? userMap[r.to_value] : undefined,
      subtask_id: r.task_id,
      subtask_title: titleById[r.task_id] || "",
    }));
    return { data: mapToMongo(merged) };
  }

  // Grouped by skill name across everyone, bucketed by level — matches
  // what SkillsPage.jsx's "Team Matrix" tab expects (this used to be a
  // hardcoded { matrix: [] } stub, wrong key name too — the tab has never
  // shown real data).
  if (url === "/skills/matrix") {
    const [{ data: rows }, { data: skillUsers }] = await Promise.all([
      supabase.from("user_skills").select("user_id, name, level"),
      supabase.from("users_public").select("id, name"),
    ]);
    const nameById = Object.fromEntries((skillUsers || []).map((u) => [u.id, u.name]));
    const bySkill = {};
    (rows || []).forEach((r) => {
      if (!bySkill[r.name]) bySkill[r.name] = { skill: r.name, beginner: [], moderate: [], proficient: [], total: 0 };
      (bySkill[r.name][r.level] ||= []).push({ user_id: r.user_id, name: nameById[r.user_id] || "Unknown" });
      bySkill[r.name].total++;
    });
    return { data: { skills: Object.values(bySkill).sort((a, b) => b.total - a.total) } };
  }

  // TeamMemberDetailPage.jsx expects {events, timeline} (a chronological
  // event log plus a running beginner/moderate/proficient count snapshot
  // after each one) — not a bare row array. This request had no handler
  // at all before, so it fell through to the generic dispatch, which read
  // "/users" off the URL and returned this member's own profile object
  // instead of any skill history.
  if (/^\/users\/[^/]+\/skill-history$/.test(url)) {
    const uid = url.split("/")[2];
    const { data: rows, error } = await supabase.from("skill_history").select("*").eq("user_id", uid).order("changed_at", { ascending: true });
    if (error) throw error;
    const rank = { beginner: 1, moderate: 2, proficient: 3 };
    const current = {};
    const events = [];
    const timeline = [];
    (rows || []).forEach((r) => {
      const action = !r.from_level ? "added"
        : rank[r.to_level] > rank[r.from_level] ? "upgraded"
        : rank[r.to_level] < rank[r.from_level] ? "downgraded" : "updated";
      events.push({ at: r.changed_at, action, skill: r.name, previous_level: r.from_level, level: r.to_level });
      current[r.name] = r.to_level;
      const counts = { beginner: 0, moderate: 0, proficient: 0 };
      Object.values(current).forEach((lvl) => { counts[lvl]++; });
      timeline.push({ at: r.changed_at, ...counts });
    });
    return { data: { events, timeline } };
  }

  // 2. Standard Table Query
  const table = getTableName(url);
  if (!table) {
    throw new Error(`Unsupported API route: GET ${url}`);
  }

  // Reads of the users table go through the users_public view — it never
  // exposes password_hash / two_factor_secret / two_factor_backup_hashes,
  // unlike the base table. Writes still target the base table below.
  const readTable = table === "users" ? "users_public" : table;
  let query = supabase.from(readTable).select("*");
  const resourceId = getResourceId(url, table);

  if (resourceId) {
    query = query.eq("id", resourceId).single();
  } else {
    // Handle query parameters (e.g. ?initiative_id=... or ?project_id=...)
    const urlObj = new URL(url, "http://localhost");
    urlObj.searchParams.forEach((val, key) => {
      const dbKey = key === "initiative_id" ? "initiative_id" : key === "project_id" ? "project_id" : key === "assignee_id" ? "assignee_id" : key;
      if (val && val !== "null") {
        query = query.eq(dbKey, val);
      }
    });
  }

  const { data, error } = await query;
  if (error) throw error;

  // SkillsPage.jsx expects each user row to carry its skills embedded
  // (u.skills / selectedUser.skills) — user_skills is a separate table,
  // never actually joined in before, so that was always undefined.
  if (table === "users") {
    const rows = resourceId ? (data ? [data] : []) : (data || []);
    const ids = rows.map((r) => r.id).filter(Boolean);
    if (ids.length > 0) {
      const { data: skillRows } = await supabase.from("user_skills").select("id, user_id, name, level").in("user_id", ids);
      const byUser = {};
      (skillRows || []).forEach((s) => {
        (byUser[s.user_id] ||= []).push({ id: s.id, name: s.name, level: s.level });
      });
      rows.forEach((r) => { r.skills = byUser[r.id] || []; });
    }
    return { data: mapToMongo(resourceId ? rows[0] : rows) };
  }

  return { data: mapToMongo(data) };
};

// Emulate Axios POST requests via Supabase client
client.post = async function(url, body, config) {

  // Add a company public holiday. Admin only — enforced by RLS on
  // public_holidays, not just by the Leaves page hiding the button.
  if (url === "/holidays") {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from("public_holidays")
      .insert({ holiday_date: body.holiday_date, name: body.name, created_by: user?.id })
      .select()
      .single();
    if (error) throw error;
    return { data: mapToMongo(data) };
  }

  // chat_conversations.user_id is NOT NULL and RLS requires user_id =
  // auth.uid() on insert — the generic dispatch below only ever sent
  // {title}, missing the one column the row needs, so every "New chat"
  // click failed with a 403 (RLS check violation) before it could even
  // hit the not-null constraint.
  if (url === "/chat/conversations") {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from("chat_conversations")
      .insert({ title: body.title, user_id: user?.id })
      .select()
      .single();
    if (error) throw error;
    return { data: mapToMongo(data) };
  }

  // Sends a chat message and gets Claude's reply — must come before the
  // generic dispatch below, which read this as an insert straight into
  // chat_conversations (matching on the "/chat/conversations" prefix) with
  // a {content} payload that doesn't even belong to that table. The real
  // work — persisting both turns and calling Anthropic — happens in the
  // ai-chat edge function under the service role, since a regular user's
  // JWT is only allowed to insert role='user' rows (RLS), not the
  // assistant's reply.
  if (/^\/chat\/conversations\/[^/]+\/messages$/.test(url)) {
    const convId = url.split("/")[3];
    const { data, error } = await supabase.functions.invoke("ai-chat", { body: { conversation_id: convId, content: body.content } });
    if (error) throw error;
    if (data?.error === "no_api_key") throw new Error("Add your Anthropic API key in Account settings before using Ask AI.");
    if (data?.error === "invalid_api_key") throw new Error("Your Anthropic API key was rejected — check it in Account settings.");
    if (data?.error) throw new Error(typeof data.error === "string" ? data.error : JSON.stringify(data.error));
    return { data: mapToMongo(data) };
  }

  // Save (or replace) the caller's own Anthropic API key.
  if (url === "/account/ai-key") {
    const { data, error } = await supabase.functions.invoke("manage-ai-key", { body: { action: "set_key", api_key: body.api_key } });
    if (error) throw error;
    if (data?.error) throw new Error(typeof data.error === "string" ? data.error : JSON.stringify(data.error));
    return { data };
  }

  /*
    Generate a digest.

    This is the bug behind "Something went wrong. Please try again." on the
    Digest page: POST /digest/generate matched no route, fell through to the
    generic dispatch, and became `supabase.from("digests").insert(undefined)` —
    the page never called the weekly-digest edge function at all, so no digest
    could ever be produced.
  */
  if (url === "/digest/generate" || url === "/digest/schedule/run-now") {
    // scope: { initiative_ids: [], project_ids: [] } — empty means the whole
    // portfolio.
    return { data: await runWeeklyDigest(body?.scope) };
  }

  /*
    "Test schedule now" — forces the ONE named schedule through the real
    run-scheduled-comms dispatcher (generate + send, respecting its own
    enabled/recipients/cc/bcc), instead of waiting for its configured
    day/hour to come around. See run-scheduled-comms/index.ts's testOnly
    branch: it never forces any other schedule in the system.
  */
  if (url === "/digest/schedule/test") {
    const data = await invokeFunction("run-scheduled-comms", { test_scope: "digest" });
    return { data: data?.digest };
  }
  if (url === "/reports/schedule/test") {
    const data = await invokeFunction("run-scheduled-comms", { test_scope: "report", schedule_id: body?.schedule_id });
    return { data: (data?.reports || [])[0] };
  }

  /*
    Email a digest to a list of recipients through the send-digest edge
    function, which uses the shared Microsoft Graph mailer — the same path as
    every other outbound mail in this app. Resend is not used here despite the
    weekly-digest function still carrying its own Resend branch.
  */
  /*
    Email a report, the same way /digest/send emails a digest.

    The function reads the report from the database rather than taking HTML from
    here, so this cannot become a way to send arbitrary mail from the org's
    mailbox — see supabase/functions/send-report/index.ts.
  */
  if (url === "/reports/send") {
    return {
      data: await invokeFunction("send-report", {
        report_id: body?.report_id,
        /*
          All three fields are forwarded.

          This route used to pass only `recipients`, which nothing sends any
          more — the send panel was split into To/Cc/Bcc and the edge function
          was taught to read them, but this shim in the middle kept forwarding
          the one old key. Every address was therefore dropped in transit.
          `recipients` is still passed through for a browser tab still running
          an older bundle, where it is treated as To.
        */
        to: body?.to || [],
        cc: body?.cc || [],
        bcc: body?.bcc || [],
        recipients: body?.recipients || [],
        // Optional free-text note shown above the report body — see
        // ReportsPage.jsx's "Message" box and send-report/index.ts.
        message: body?.message || "",
      }),
    };
  }

  // Archive a report/digest for the current user only — inserting into the
  // per-user join table, not touching the shared reports/digests row, so
  // this never affects what any other viewer sees.
  const reportArchiveMatch = url.match(/^\/reports\/([^/]+)\/archive$/);
  if (reportArchiveMatch) {
    const [, reportId] = reportArchiveMatch;
    const { data: { user } } = await supabase.auth.getUser();
    const { error } = await supabase.from("report_archives").upsert({ user_id: user?.id, report_id: reportId });
    if (error) throw error;
    return { data: {} };
  }
  const digestArchiveMatch = url.match(/^\/digest\/([^/]+)\/archive$/);
  if (digestArchiveMatch) {
    const [, digestId] = digestArchiveMatch;
    const { data: { user } } = await supabase.auth.getUser();
    const { error } = await supabase.from("digest_archives").upsert({ user_id: user?.id, digest_id: digestId });
    if (error) throw error;
    return { data: {} };
  }

  if (url === "/digest/send") {
    return {
      data: await invokeFunction("send-digest", {
        digest_id: body?.digest_id,
        // See /reports/send above: forwarding only `recipients` dropped every
        // address once the panel became To/Cc/Bcc.
        to: body?.to || [],
        cc: body?.cc || [],
        bcc: body?.bcc || [],
        recipients: body?.recipients || [],
        // Optional free-text note shown above the digest body — see
        // DigestPage.jsx's "Message" box and send-digest/index.ts.
        message: body?.message || "",
      }),
    };
  }

  // Adds a comment to a task's discussion thread.
  if (/^\/tasks\/[^/]+\/comments$/.test(url)) {
    const taskId = url.split("/")[2];
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from("task_comments")
      .insert({ task_id: taskId, user_id: user?.id, body: body.body })
      .select()
      .single();
    if (error) throw error;
    return { data: mapToMongo(data) };
  }

  // Adds an additional assignee beyond tasks.assignee_id (the primary).
  if (/^\/tasks\/[^/]+\/assignees$/.test(url)) {
    const taskId = url.split("/")[2];
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from("task_assignees")
      .insert({ task_id: taskId, user_id: body.user_id, added_by: user?.id })
      .select()
      .single();
    if (error) throw error;
    return { data };
  }

  // Had no handler at all — fell through to the generic table dispatch,
  // which reads "/users" off the front of this URL and mismapped the
  // whole request onto the users table itself (hence the 400: a
  // {name, level} payload has no matching columns there).
  if (/^\/users\/[^/]+\/skills$/.test(url)) {
    const uid = url.split("/")[2];
    const { data, error } = await supabase.from("user_skills").insert({ user_id: uid, name: body.name, level: body.level || "beginner" }).select().single();
    if (error) throw error;
    return { data: mapToMongo(data) };
  }

  if (url === "/auth/change-password") {
    const { data, error } = await supabase.auth.updateUser({ password: body.new_password });
    if (error) throw error;
    return { data };
  }

  // Had no handler at all — every "Deactivate"/"Re-enable" click on
  // TeamPage.jsx has been throwing "Unsupported API route" outright. No
  // .select() here for the same reason as the users PATCH branch below:
  // authenticated has no SELECT grant on the base users table.
  if (url === "/users/bulk-status") {
    const { data: { user } } = await supabase.auth.getUser();
    const requestedIds = body.user_ids || [];
    const ids = requestedIds.filter((id) => id !== user?.id);
    const skipped_self = ids.length !== requestedIds.length;
    if (ids.length > 0) {
      const { error } = await supabase.from("users").update({ disabled: !!body.disabled }).in("id", ids);
      if (error) throw error;
    }
    return { data: { modified: ids.length, skipped_self } };
  }

  // Had no handler at all — fell through to the generic table dispatch,
  // which reads "/projects" off the front of this URL and tried to INSERT
  // a brand-new project row with {title, url} (hence the 400: those aren't
  // real columns together, and initiative_id/status are required). Related
  // documents actually live in projects.document_links (a jsonb array).
  if (/^\/projects\/[^/]+\/documents$/.test(url)) {
    const projectId = url.split("/")[2];
    const { data: proj, error: getErr } = await supabase.from("projects").select("document_links").eq("id", projectId).single();
    if (getErr) throw getErr;
    const links = [...(proj.document_links || []), { title: body.title, url: body.url }];
    const { data, error } = await supabase.from("projects").update({ document_links: links }).eq("id", projectId).select().single();
    if (error) throw error;
    return { data: mapToMongo(data) };
  }

  if (/^\/projects\/[^/]+\/members$/.test(url) || /^\/initiatives\/[^/]+\/members$/.test(url)) {
    const isInitiative = url.startsWith("/initiatives/");
    const table = isInitiative ? "initiative_members" : "project_members";
    const fkCol = isInitiative ? "initiative_id" : "project_id";
    const parentId = url.split("/")[2];
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from(table)
      .insert({ [fkCol]: parentId, user_id: body.user_id, added_by: user?.id })
      .select()
      .single();
    if (error) throw error;
    return { data };
  }

  // Real AI report generation — calls the generate-report edge function,
  // which gathers the actual project/member data and writes a real Claude
  // report (replaces the old hardcoded mock string).
  if (url === "/reports/generate") {
    const { data, error } = await supabase.functions.invoke("generate-report", { body: { scope: body.scope, target_id: body.target_id, week_start: body.week_start } });

    // generate-report signals every failure with a non-2xx status, which means
    // `data` is null and `error` is a generic FunctionsHttpError — checking
    // data?.error alone (as this did) could never match, so a missing API key,
    // an authorization refusal and an upstream Anthropic failure all surfaced
    // as the same "Edge Function returned a non-2xx status code". Read the real
    // body off error.context first, then map it.
    const payload = await resolveFunctionPayload(error, data);
    const code = payload?.error;
    if (code === "no_api_key") throw new Error("Add your Anthropic API key in Account settings before generating AI reports.");
    if (code === "invalid_api_key") throw new Error("Your Anthropic API key was rejected — check it in Account settings.");
    if (code === "forbidden") throw new Error("You don't have access to generate a report for this target.");
    if (code === "report_too_large") throw new Error("This project has too much history to summarise in one report. Trim or archive its oldest task comments and try again.");
    if (code === "empty_report") throw new Error("The model returned an empty report. Try generating again.");
    if (code) throw new Error(`Report generation failed: ${describeFunctionError(code)}`);
    if (error) throw error;
    // Reshaped to the field names ReportsPage.jsx expects (scope/target_id/
    // created_by/created_at) rather than the real column names — same
    // reshape applied on the GET /reports list below, so both paths agree.
    return { data: mapToMongo({ ...data, scope: data.scope_type, target_id: data.scope_id, created_by: data.generated_by, created_at: data.generated_at }) };
  }

  if (url.startsWith("/users/") && url.endsWith("/leaves")) {
    const uid = url.split("/")[2];
    // Only real leaves columns go to Postgres — user_name/user_role/days
    // used to be stuffed into this same payload despite none of the three
    // being actual columns on the table, so every insert failed outright.
    const payload = {
      user_id: uid,
      type: body.type,
      start_date: body.start_date,
      end_date: body.end_date,
      reason: body.reason || null,
      status: body.status || "planned",
      manager_id: body.manager_id || null,
    };
    const { data, error } = await supabase.from("leaves").insert(payload).select().single();
    if (error) throw error;
    // Best-effort: the leave is already created regardless of whether this
    // email goes out. notify-leave re-reads the row itself, so nothing here
    // needs to duplicate the applicant/manager lookup.
    invokeFunction("notify-leave", { action: "request", leave_id: data.id })
      .catch((e) => console.error("notify-leave (request) failed:", e));
    const [enriched] = await enrichLeaves([data]);
    return { data: mapToMongo(enriched) };
  }

  // Approve / reject a task's "go Live" request. The rule is enforced in
  // decide_live_approval(); the email back to the requester is best-effort.
  const liveDecisionMatch = url.match(/^\/live-approvals\/([^/]+)\/decide$/);
  if (liveDecisionMatch) {
    const requestId = liveDecisionMatch[1];
    const { data, error } = await supabase.rpc("decide_live_approval", {
      p_request_id: requestId,
      p_approve: !!body?.approve,
      p_note: body?.note || null,
    });
    if (error) throw error;
    invokeFunction("notify-live-approval", { action: "decision", request_id: requestId })
      .catch((e) => console.error("notify-live-approval (decision) failed:", e));
    return { data };
  }

  const table = getTableName(url);
  if (!table) {
    throw new Error(`Unsupported API route: POST ${url}`);
  }

  const payload = mapToPostgres(body);
  const { data, error } = await supabase.from(table).insert(payload).select().single();
  if (error) throw error;
  return { data: mapToMongo(data) };
};

// Emulate Axios PATCH requests via Supabase client
client.patch = async function(url, body, config) {

  // Saving the weekly schedule. api.put delegates here, so this covers both.
  // The row is a singleton: updated in place when it exists, inserted the first
  // time. Only real columns are written — the UI's `timezone` is display-only
  // (pg_cron runs in UTC and the table has no timezone column), so sending it
  // would fail with "column does not exist".
  if (url === "/digest/schedule") {
    /*
      Saved through the manage-digest-schedule Edge Function, NOT a direct
      client write.

      A direct insert here failed with "new row violates row-level security
      policy for table digest_schedule": that table has SELECT and UPDATE
      policies but no INSERT policy, and it starts out empty, so the first
      write — necessarily an insert — was denied for everyone, admins included.
      The function does it under the service role after its own admin/manager
      check, which is the pattern CLAUDE.md prescribes for privileged writes.
    */
    const data = await invokeFunction("manage-digest-schedule", {
      action: "save",
      enabled: !!body?.enabled,
      recipients: body?.recipients || [],
      cc: body?.cc || [],
      bcc: body?.bcc || [],
      day_of_week: body?.day_of_week,
      hour: body?.hour,
      minute: body?.minute,
      scope_initiative_ids: body?.scope_initiative_ids || [],
      scope_project_ids: body?.scope_project_ids || [],
      scope_user_ids: body?.scope_user_ids || [],
    });
    // Surfaced, not swallowed: the one case that produces a warning is the
    // scope columns being absent, and its symptom in the UI is a scope tick
    // that silently un-ticks itself. A console.warn nobody opens is no better
    // than nothing.
    return { data: { ...scheduleFromRow(data?.schedule), warning: data?.warning || null } };
  }

  // Saving a per-project/per-member report schedule. Same reasoning as
  // /digest/schedule above: goes through the Edge Function under the
  // service role, not a direct client write, since report_schedules has no
  // INSERT/UPDATE RLS policy for the first save to satisfy.
  if (url === "/reports/schedule") {
    const data = await invokeFunction("manage-report-schedule", {
      action: "save",
      scope_type: body?.scope_type,
      scope_id: body?.scope_id,
      enabled: !!body?.enabled,
      recipients: body?.recipients || [],
      cc: body?.cc || [],
      bcc: body?.bcc || [],
      day_of_week: body?.day_of_week,
      hour: body?.hour,
      minute: body?.minute,
    });
    return { data: reportScheduleFromRow(data?.schedule) };
  }


  // Must come before the generic dispatch below — getTableName("/users/.../
  // skills/...") also resolves to "users" (it only checks the prefix), and
  // getResourceId would grab whichever UUID it finds first (the user id or
  // the skill id), silently PATCHing the wrong table/row.
  const skillsMatch = url.match(/^\/users\/[^/]+\/skills\/([^/]+)$/);
  if (skillsMatch) {
    const [, skillId] = skillsMatch;
    const { data, error } = await supabase.from("user_skills").update({ level: body.level }).eq("id", skillId).select().single();
    if (error) throw error;
    return { data: mapToMongo(data) };
  }

  const table = getTableName(url);
  const resourceId = getResourceId(url, table);
  if (!table || !resourceId) {
    throw new Error(`Unsupported API route: PATCH ${url}`);
  }

  const payload = mapToPostgres(body);

  /*
    Going Live needs the project owner's / manager's approval
    (supabase/schema_task_live_approval.sql). The database refuses a direct
    status = 'live', so a request to set it is turned into an approval REQUEST
    here: the task stays where it is and the approvers are emailed. Any other
    fields sent in the same PATCH are still saved.
  */
  if (table === "tasks" && payload.status === "live") {
    const { data: current, error: curErr } = await supabase.from("tasks").select("*").eq("id", resourceId).single();
    if (curErr) throw curErr;
    if (current.status !== "live") {
      const { data: requested, error: reqErr } = await supabase.rpc("request_live_approval", { p_task_id: resourceId });
      if (reqErr) throw reqErr;
      if (requested?.created) {
        invokeFunction("notify-live-approval", { action: "request", request_id: requested.id })
          .catch((e) => console.error("notify-live-approval (request) failed:", e));
      }
      toast.info(
        requested?.created
          ? "Sent for approval — the project owner/manager has been emailed. The task goes Live once they approve."
          : "Already waiting for approval.",
      );
      delete payload.status;
      if (Object.keys(payload).length === 0) return { data: mapToMongo(current) };
    }
  }

  // authenticated has UPDATE but deliberately NO SELECT on the base users
  // table (that's the whole point of the users-lockdown fix — password_hash/
  // two_factor_secret/etc. must never come back over the wire). The plain
  // .update().select() below always asks PostgREST to RETURNING the row
  // afterward, which silently needs SELECT and fails with "permission
  // denied for table users" regardless of RLS — this was breaking every
  // direct users-table PATCH (role changes, disable/enable) outright.
  // Update without asking for a return value, then re-read the fresh row
  // from users_public instead, which IS safely readable.
  if (table === "users") {
    const { error } = await supabase.from("users").update(payload).eq("id", resourceId);
    if (error) throw error;
    const { data, error: selErr } = await supabase.from("users_public").select("*").eq("id", resourceId).single();
    if (selErr) throw selErr;
    return { data: mapToMongo(data) };
  }

  const { data, error } = await supabase.from(table).update(payload).eq("id", resourceId).select().single();
  if (error) throw error;
  // A decision (approve/reject), not a plain edit of type/dates/reason —
  // notify the applicant. Best-effort, after the status change has already
  // succeeded; notify-leave re-reads the row itself.
  if (table === "leaves" && (payload.status === "approved" || payload.status === "rejected")) {
    invokeFunction("notify-leave", { action: "decision", leave_id: resourceId })
      .catch((e) => console.error("notify-leave (decision) failed:", e));
  }
  return { data: mapToMongo(data) };
};

// Emulate Axios PUT requests via Supabase client
client.put = async function(url, body, config) {
  return client.patch(url, body, config);
};

// Emulate Axios DELETE requests via Supabase client
client.delete = async function(url, config) {
  const holidayMatch = url.match(/^\/holidays\/([^/]+)$/);
  if (holidayMatch) {
    const { error } = await supabase.from("public_holidays").delete().eq("id", holidayMatch[1]);
    if (error) throw error;
    return { data: {} };
  }
  if (url === "/account/ai-key") {
    const { data, error } = await supabase.functions.invoke("manage-ai-key", { body: { action: "clear_key" } });
    if (error) throw error;
    if (data?.error) throw new Error(typeof data.error === "string" ? data.error : JSON.stringify(data.error));
    return { data };
  }
  const projectDocDeleteMatch = url.match(/^\/projects\/([^/]+)\/documents\/([^/]+)$/);
  if (projectDocDeleteMatch) {
    const [, projectId, idxStr] = projectDocDeleteMatch;
    const idx = Number(idxStr);
    const { data: proj, error: getErr } = await supabase.from("projects").select("document_links").eq("id", projectId).single();
    if (getErr) throw getErr;
    const links = (proj.document_links || []).filter((_, i) => i !== idx);
    const { error } = await supabase.from("projects").update({ document_links: links }).eq("id", projectId);
    if (error) throw error;
    return { data: {} };
  }
  const projectMembersMatch = url.match(/^\/projects\/([^/]+)\/members\/([^/]+)$/);
  if (projectMembersMatch) {
    const [, projectId, userId] = projectMembersMatch;
    const { error } = await supabase.from("project_members").delete().eq("project_id", projectId).eq("user_id", userId);
    if (error) throw error;
    return { data: {} };
  }
  const initiativeMembersMatch = url.match(/^\/initiatives\/([^/]+)\/members\/([^/]+)$/);
  if (initiativeMembersMatch) {
    const [, initiativeId, userId] = initiativeMembersMatch;
    const { error } = await supabase.from("initiative_members").delete().eq("initiative_id", initiativeId).eq("user_id", userId);
    if (error) throw error;
    return { data: {} };
  }
  const skillsDeleteMatch = url.match(/^\/users\/[^/]+\/skills\/([^/]+)$/);
  if (skillsDeleteMatch) {
    const [, skillId] = skillsDeleteMatch;
    const { error } = await supabase.from("user_skills").delete().eq("id", skillId);
    if (error) throw error;
    return { data: {} };
  }
  const taskAssigneeDeleteMatch = url.match(/^\/tasks\/([^/]+)\/assignees\/([^/]+)$/);
  if (taskAssigneeDeleteMatch) {
    const [, taskId, userId] = taskAssigneeDeleteMatch;
    const { error } = await supabase.from("task_assignees").delete().eq("task_id", taskId).eq("user_id", userId);
    if (error) throw error;
    return { data: {} };
  }
  const taskCommentDeleteMatch = url.match(/^\/tasks\/[^/]+\/comments\/([^/]+)$/);
  if (taskCommentDeleteMatch) {
    const [, commentId] = taskCommentDeleteMatch;
    const { error } = await supabase.from("task_comments").delete().eq("id", commentId);
    if (error) throw error;
    return { data: {} };
  }
  const reportUnarchiveMatch = url.match(/^\/reports\/([^/]+)\/archive$/);
  if (reportUnarchiveMatch) {
    const [, reportId] = reportUnarchiveMatch;
    const { data: { user } } = await supabase.auth.getUser();
    const { error } = await supabase.from("report_archives").delete().eq("user_id", user?.id).eq("report_id", reportId);
    if (error) throw error;
    return { data: {} };
  }
  const digestUnarchiveMatch = url.match(/^\/digest\/([^/]+)\/archive$/);
  if (digestUnarchiveMatch) {
    const [, digestId] = digestUnarchiveMatch;
    const { data: { user } } = await supabase.auth.getUser();
    const { error } = await supabase.from("digest_archives").delete().eq("user_id", user?.id).eq("digest_id", digestId);
    if (error) throw error;
    return { data: {} };
  }

  const table = getTableName(url);
  const resourceId = getResourceId(url, table);
  if (!table || !resourceId) {
    throw new Error(`Unsupported API route: DELETE ${url}`);
  }

  const { data, error } = await supabase.from(table).delete().eq("id", resourceId);
  if (error) throw error;
  return { data: mapToMongo(data) };
};

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  
  if (detail instanceof Error || (typeof detail === "object" && detail !== null)) {
    if (detail.response?.data?.detail) {
      return formatApiErrorDetail(detail.response.data.detail);
    }
    if (detail.message) {
      return detail.message;
    }
    if (detail.error_description) {
      return detail.error_description;
    }
  }

  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const API = "";
export default client;
